import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Typography, TextField, useMediaQuery, Theme } from "@mui/material";
import { useSnackbar } from "notistack";
import {
  Body,
  BottomActionArea,
  BottomActionButton,
  Header,
  Modal,
  NavigateBeforeButton,
} from "@components";
import { TemplateType } from "@interfaces";
import {
  useTemplateCreationContext,
  queryClient,
  useMedicineSearchContext,
} from "@contexts";
import { useCreateTemplate } from "@hooks";
import { PrescriptionDetailCard } from "./components/PrescriptionDetailCard";
import {
  AddMedicineButton,
  ContentContainer,
  EmptyRequest,
  Footer,
  HeaderContainer,
  PrescriptionContent,
  PrescriptionDetailsContainer,
  RouterLink,
  SectionSubtitle,
  Title,
} from "./styles/PrescriptionCreate.styles";
import { StyledInfoIconSecondary } from "@styles/styled";

export function TemplateSummary() {
  const navigate = useNavigate();
  const { requestId = "" } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { setSearchText } = useMedicineSearchContext();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );

  const { templateDetails, removeDetail, clearDetails } =
    useTemplateCreationContext();
  const { mutateAsync: createTemplate, isPending: isSaving } =
    useCreateTemplate();

  const heldDetails = useMemo(
    () => templateDetails.filter((d) => d.prescriptionType === "HELD"),
    [templateDetails],
  );
  const simpleDetails = useMemo(
    () => templateDetails.filter((d) => d.prescriptionType !== "HELD"),
    [templateDetails],
  );

  const [showNameModal, setShowNameModal] = useState(false);
  const [templateName, setTemplateName] = useState("");

  useEffect(() => {
    setSearchText("");
  }, [setSearchText]);

  const handleBack = useCallback(() => {
    navigate(`/app/solicitudes/${requestId}/receta/crear/buscar`);
  }, [navigate, requestId]);

  const handleSave = useCallback(async () => {
    if (!templateName.trim()) return;
    try {
      await createTemplate({
        name: templateName.trim(),
        templateType: TemplateType.PRESCRIPTION,
        prescriptionDetails: templateDetails.map(
          ({ localId, ...detail }) => detail,
        ),
      });
      await queryClient.invalidateQueries({ queryKey: ["templates"] });
      clearDetails();
      setShowNameModal(false);
      setTemplateName("");
      enqueueSnackbar("Plantilla guardada exitosamente", {
        variant: "success",
      });
      navigate(`/app/solicitudes/${requestId}/receta/crear/buscar`);
    } catch (error) {
      console.error("Error creating template:", error);
      enqueueSnackbar("Error al guardar la plantilla", { variant: "error" });
    }
  }, [
    templateName,
    createTemplate,
    templateDetails,
    clearDetails,
    enqueueSnackbar,
    navigate,
    requestId,
  ]);

  return (
    <>
      <Header
        title={isMobile ? "Resumen plantilla" : undefined}
        useMekiDocIcon={!isMobile}
        titleColor="black"
        left={<NavigateBeforeButton onClick={handleBack} />}
      />
      <ContentContainer disableGutters>
        <HeaderContainer>
          <Title variant="h4" fontWeight={"medium"}>
            Resumen plantilla
          </Title>
          <RouterLink to={"tratamiento/buscar"}>
            <AddMedicineButton variant="outlined" size="large">
              <Typography variant="body1" fontWeight={"medium"}>
                Agregar medicamento
              </Typography>
            </AddMedicineButton>
          </RouterLink>
        </HeaderContainer>

        <Body>
          <PrescriptionContent>
            {templateDetails.length > 0 ? (
              <>
                {heldDetails.length > 0 && (
                  <>
                    <SectionSubtitle>Receta retenida</SectionSubtitle>
                    <PrescriptionDetailsContainer>
                      {heldDetails.map((detail) => (
                        <PrescriptionDetailCard
                          key={detail.localId}
                          detail={detail}
                          handleDelete={() => removeDetail(detail.localId)}
                        />
                      ))}
                    </PrescriptionDetailsContainer>
                  </>
                )}
                {simpleDetails.length > 0 && (
                  <>
                    <SectionSubtitle>Receta simple</SectionSubtitle>
                    <PrescriptionDetailsContainer>
                      {simpleDetails.map((detail) => (
                        <PrescriptionDetailCard
                          key={detail.localId}
                          detail={detail}
                          handleDelete={() => removeDetail(detail.localId)}
                        />
                      ))}
                    </PrescriptionDetailsContainer>
                  </>
                )}
                <Footer>
                  <BottomActionArea
                    style={{ backgroundColor: "inherit", border: "none" }}
                  >
                    <BottomActionButton
                      style={{ width: "100%" }}
                      onClick={() => setShowNameModal(true)}
                    >
                      Guardar plantilla
                    </BottomActionButton>
                  </BottomActionArea>
                </Footer>
              </>
            ) : (
              <EmptyRequest>
                <StyledInfoIconSecondary />
                No tienes medicamentos agregados
              </EmptyRequest>
            )}
          </PrescriptionContent>
        </Body>
      </ContentContainer>

      <Modal
        title="Nombre de la plantilla"
        isOpen={showNameModal}
        onClose={() => setShowNameModal(false)}
        buttonProps={{
          buttonText: "Guardar",
          onClick: handleSave,
          loading: isSaving,
          disabled: !templateName.trim(),
        }}
      >
        <TextField
          fullWidth
          autoFocus
          placeholder="Ej: Hipertensión arterial"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          sx={{ marginY: "30px" }}
        />
      </Modal>
    </>
  );
}
